import axios from 'axios';
import { 
  PricingRequest, 
  PricingResponse, 
  BookingRequest, 
  BookingResponse,
  AvailabilityResponse,
  AvailabilityTableResponse,
  ContactFormData,
  ContactResponse
} from '../types/api';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.lcgfreight.com';
const API_KEY = process.env.REACT_APP_API_KEY;

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Authorization': `Bearer ${API_KEY}`,
    'Content-Type': 'application/json',
  },
});

// Add response interceptor for error handling
api.interceptors.response.use(
  response => response,
  error => {
    // For demo/development, return mock data if API is not available
    if (process.env.NODE_ENV === 'development') {
      console.log('Using mock data in development mode');
      
      if (error.config.url.includes('/location/request')) {
        return Promise.resolve({
          data: {
            status: 'Success',
            message: 'Rate successfully retrieved.',
            data: {
              quoteId: Date.now(),
              city: {
                name: error.config.data.city,
                state: error.config.data.state,
                stateName: 'State Name',
                _id: '123'
              },
              zipCode: error.config.data.zipCode,
              cityFullName: `${error.config.data.city}, ${error.config.data.state}`,
              terminal: {
                terminalKey: error.config.data.terminalKey,
                location: 'Terminal Location'
              },
              onewayMiles: 213,
              travelTime: { fullTime: '3 hours 41 mins' },
              statesCrossed: 2,
              charges: {
                ItemPrice: 500,
                fscSurcharge: 50,
                chassisDays: 2,
                mileageSurcharge: 0,
                tolls: 25,
                reeferFee: error.config.data.isReefer ? 200 : 0,
                bulkLiquidFee: error.config.data.isBulkLiquid ? 200 : 0,
                hazFee: error.config.data.ishazFee ? 200 : 0,
                overweightTotal: error.config.data.overWeight ? 300 : 0
              },
              chassis: {
                containerSize: parseInt(error.config.data.containerSize),
                chassisType: 'Standard',
                rate: 25,
                minDays: 2
              },
              quoteTotalPrice: 979,
              overweight: error.config.data.overWeight ? {
                charges: [{ name: 'Overweight Fee', rate: 300 }]
              } : null
            }
          }
        });
      }
      
      if (error.config.url.includes('/availability/weekly')) {
        return Promise.resolve({
          data: {
            status: 'Success',
            message: 'Availability retrieved.',
            data: {
              weeklyAvailability: {
                [new Date().toISOString().split('T')[0]]: { available: 8, total: 10 },
                [new Date(Date.now() + 86400000).toISOString().split('T')[0]]: { available: 6, total: 10 },
                [new Date(Date.now() + 172800000).toISOString().split('T')[0]]: { available: 9, total: 10 },
                [new Date(Date.now() + 259200000).toISOString().split('T')[0]]: { available: 4, total: 10 },
                [new Date(Date.now() + 345600000).toISOString().split('T')[0]]: { available: 7, total: 10 },
                [new Date(Date.now() + 432000000).toISOString().split('T')[0]]: { available: 10, total: 10 },
                [new Date(Date.now() + 518400000).toISOString().split('T')[0]]: { available: 5, total: 10 }
              }
            }
          }
        });
      }
    }
    return Promise.reject(error);
  }
);

export const getPricing = async (request: PricingRequest): Promise<PricingResponse> => {
  try {
    const response = await api.post('/location/request', request);
    return response.data;
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      // Return mock data directly if API call fails in development
      return {
        status: 'Success',
        message: 'Rate successfully retrieved.',
        data: {
          quoteId: Date.now(),
          city: {
            name: request.city,
            state: request.state,
            stateName: 'State Name',
            _id: '123'
          },
          zipCode: request.zipCode,
          cityFullName: `${request.city}, ${request.state}`,
          terminal: {
            terminalKey: request.terminalKey,
            location: 'Terminal Location'
          },
          onewayMiles: 213,
          travelTime: { fullTime: '3 hours 41 mins' },
          statesCrossed: 2,
          charges: {
            ItemPrice: 500,
            fscSurcharge: 50,
            chassisDays: 2,
            mileageSurcharge: 0,
            tolls: 25,
            reeferFee: request.isReefer ? 200 : 0,
            bulkLiquidFee: request.isBulkLiquid ? 200 : 0,
            hazFee: request.ishazFee ? 200 : 0,
            overweightTotal: request.overWeight ? 300 : 0
          },
          chassis: {
            containerSize: parseInt(request.containerSize),
            chassisType: 'Standard',
            rate: 25,
            minDays: 2
          },
          quoteTotalPrice: 979,
          overweight: request.overWeight ? {
            charges: [{ name: 'Overweight Fee', rate: 300 }]
          } : null
        }
      } as PricingResponse;
    }
    throw error;
  }
};

export const createBooking = async (request: BookingRequest): Promise<BookingResponse> => {
  const response = await api.post('/booking/create', request);
  return response.data;
};

export const getAvailability = async (): Promise<AvailabilityResponse> => {
  const response = await api.get('/availability/weekly');
  return response.data;
};

// Updated function to get availability table data from the new API endpoint
export const getAvailabilityTableData = async (): Promise<AvailabilityTableResponse> => {
  const response = await axios.get('https://webhook.lcgfreight.com/availability/table', {
    headers: {
      'x-api-key': 'uGsecOp6KCRchTTVYfU2IOb60wmKwe',
      'Content-Type': 'application/json',
    },
  });
  return response.data;
};

export const sendContactEmail = async (data: ContactFormData): Promise<ContactResponse> => {
  try {
    const response = await axios.post('https://webhook.lcgfreight.com/contact', data, {
      headers: {
        'x-api-key': 'uGsecOp6KCRchTTVYfU2IOb60wmKwe',
        'Content-Type': 'application/json',
      },
    });

    return response.data;
  } catch (error) {
    console.error('Error sending contact email:', error);
    throw error;
  }
};

// Polling interval for real-time updates (in milliseconds)
export const AVAILABILITY_POLLING_INTERVAL = 30000; // 30 seconds
